import { createFileRoute, notFound } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { z } from "zod";
import { LearningPathNotFound } from "@/features/learning-paths/components/LearningPathNotFound";
import { LearningPathPage } from "@/features/learning-paths/components/LearningPathPage";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import { getLearningPathsGetPublishedQueryOptions } from "@/services/api/learning-paths/learning-paths";
import { queryClient } from "@/services/queryClient";

// Stripe Checkout returns here after buying the path: success or cancelled drives a confirmation, not the grant
// itself (the webhook grants the path). session_id is echoed back by Stripe on success.
const learningPathSearchSchema = z.object({
  checkout: z.enum(["success", "cancelled"]).optional(),
  // A checkout for seats on a Restricted path (LMS-198) returns here with purchase=seats.
  purchase: z.enum(["seats"]).optional(),
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/_marketing/learning-paths/$pathId")({
  validateSearch: learningPathSearchSchema,
  // Learning paths are hidden while their feature flag is off (the API 404s them too).
  beforeLoad: async () => {
    const { learningPaths } = await fetchFeatureFlags();
    if (!learningPaths) throw notFound();
  },
  loader: async ({ params: { pathId } }) => {
    try {
      // Always asked afresh, never served from the cache: the API also answers 404 once the path or the
      // learning-paths flag is switched off, which cached flags or a cached path would hide.
      return await queryClient.fetchQuery({
        ...getLearningPathsGetPublishedQueryOptions(pathId),
        staleTime: 0,
      });
    } catch (error) {
      // A draft path answers 404 just like one that doesn't exist.
      if (isAxiosError(error) && error.response?.status === 404) throw notFound();
      throw error;
    }
  },
  notFoundComponent: LearningPathNotFound,
  errorComponent: LearningPathError,
  component: PublishedLearningPathPage,
});

function PublishedLearningPathPage() {
  const path = Route.useLoaderData();
  const { checkout, purchase } = Route.useSearch();
  return <LearningPathPage path={path} checkout={checkout} purchase={purchase} />;
}

function LearningPathError() {
  return (
    <section className="bg-arc-surface py-section-sm md:py-section">
      <div role="alert" className="mx-auto flex max-w-3xl flex-col gap-3 px-6 text-center md:px-8">
        <h1 className="text-display-sm text-arc-gray-900 font-semibold">
          We couldn't load this learning path
        </h1>
        <p className="text-lead text-arc-gray-600">Please reload the page to try again.</p>
      </div>
    </section>
  );
}
